"use client";

/**
 * @module Drawer
 *
 * Bottom sheet drawer for mobile-friendly interactions. Built on Vaul library.
 * Supports drag-to-dismiss, snap points, and background scaling.
 *
 * @see {@link https://ui.shadcn.com/docs/components/drawer Shadcn Drawer}
 * @see {@link https://vaul.emilkowal.ski/ Vaul Documentation}
 *
 * @example
 * // Basic drawer
 * <Drawer>
 *   <DrawerTrigger asChild>
 *     <Button>Open Drawer</Button>
 *   </DrawerTrigger>
 *   <DrawerContent>
 *     <DrawerHeader>
 *       <DrawerTitle>Settings</DrawerTitle>
 *       <DrawerDescription>Adjust your preferences</DrawerDescription>
 *     </DrawerHeader>
 *     <div className="p-4">Content here</div>
 *     <DrawerFooter>
 *       <DrawerClose asChild>
 *         <Button variant="outline">Close</Button>
 *       </DrawerClose>
 *     </DrawerFooter>
 *   </DrawerContent>
 * </Drawer>
 *
 * @example
 * // Controlled drawer with custom handle
 * const [open, setOpen] = useState(false);
 *
 * <Drawer open={open} onOpenChange={setOpen}>
 *   <DrawerContent handleColor="dark">
 *     <DrawerTitle>Menu</DrawerTitle>
 *   </DrawerContent>
 * </Drawer>
 */
import type * as React from "react";
import type { Ref } from "react";
import { Drawer as DrawerPrimitive } from "vaul";
import { cn } from "../lib/utils";

/**
 * Root drawer component. Scales background by default.
 * @param shouldScaleBackground - Scale page content when drawer opens (default: true)
 */
const Drawer = ({
  shouldScaleBackground = true,
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Root>) => <DrawerPrimitive.Root shouldScaleBackground={shouldScaleBackground} {...props} data-sentry-element="DrawerPrimitive.Root" data-sentry-component="Drawer" data-sentry-source-file="drawer.tsx" />;
Drawer.displayName = "Drawer";

/** Element that opens the drawer when clicked. Use `asChild` to wrap custom triggers. */
const DrawerTrigger = DrawerPrimitive.Trigger;

/** Portal for rendering drawer outside the DOM hierarchy. */
const DrawerPortal = DrawerPrimitive.Portal;

/** Closes the drawer when clicked. Use `asChild` to wrap custom close buttons. */
const DrawerClose = DrawerPrimitive.Close;

/** Semi-transparent backdrop behind the drawer. */
const DrawerOverlay = ({
  className,
  ref,
  ...props
}: React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Overlay> & {
  ref?: Ref<React.ElementRef<typeof DrawerPrimitive.Overlay>>;
}) => <DrawerPrimitive.Overlay className={cn("fixed inset-0 z-50 bg-black/80", className)} ref={ref} {...props} data-sentry-element="DrawerPrimitive.Overlay" data-sentry-component="DrawerOverlay" data-sentry-source-file="drawer.tsx" />;

/**
 * Main drawer content container. Slides up from bottom with drag handle.
 * @param handleColor - Handle bar color: `"default"`, `"dark"`, `"light"`, or `"custom"`
 * @param customHandleColor - Tailwind class for custom handle color (when handleColor="custom")
 */
const DrawerContent = ({
  className,
  children,
  handleColor = "default",
  customHandleColor,
  ref,
  ...props
}: React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Content> & {
  handleColor?: "default" | "dark" | "light" | "custom";
  customHandleColor?: string;
  ref?: Ref<React.ElementRef<typeof DrawerPrimitive.Content>>;
}) => {
  const getHandleColorClass = () => {
    switch (handleColor) {
      case "dark":
        return "bg-gray-300 dark:bg-gray-700";
      case "light":
        return "bg-gray-100 dark:bg-gray-900";
      case "custom":
        return customHandleColor ?? "bg-gray-200 dark:bg-gray-800";
      case "default":
      default:
        return "bg-gray-200 dark:bg-gray-800";
    }
  };
  return <DrawerPortal data-sentry-element="DrawerPortal" data-sentry-component="DrawerContent" data-sentry-source-file="drawer.tsx">
      <DrawerOverlay data-sentry-element="DrawerOverlay" data-sentry-source-file="drawer.tsx" />
      <DrawerPrimitive.Content className={cn("fixed inset-x-0 bottom-0 z-[9999] mt-16 flex h-auto flex-col rounded-t-[20px] border border-gray-200 bg-white px-6 pt-10 pb-12 dark:border-gray-800 dark:bg-gray-875", className)} ref={ref} {...props} data-sentry-element="DrawerPrimitive.Content" data-sentry-source-file="drawer.tsx">
        <div className={cn("absolute top-2 right-0 left-0 z-[9999] mx-auto h-1 w-25 rounded-full", getHandleColorClass())} />
        {children}
      </DrawerPrimitive.Content>
    </DrawerPortal>;
};

/** Header section for drawer title and description. Centered on mobile, left-aligned on desktop. */
const DrawerHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => <div className={cn("grid gap-1.5 p-4 text-center sm:text-left", className)} {...props} data-sentry-component="DrawerHeader" data-sentry-source-file="drawer.tsx" />;
DrawerHeader.displayName = "DrawerHeader";

/** Footer section for action buttons, pushed to bottom of drawer. */
const DrawerFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => <div className={cn("mt-auto flex flex-col gap-2 p-4", className)} {...props} data-sentry-component="DrawerFooter" data-sentry-source-file="drawer.tsx" />;
DrawerFooter.displayName = "DrawerFooter";

/** Drawer title with semibold weight. Required for accessibility. */
const DrawerTitle = ({
  className,
  ref,
  ...props
}: React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Title> & {
  ref?: Ref<React.ElementRef<typeof DrawerPrimitive.Title>>;
}) => <DrawerPrimitive.Title className={cn("font-semibold text-lg leading-none tracking-tight", className)} ref={ref} {...props} data-sentry-element="DrawerPrimitive.Title" data-sentry-component="DrawerTitle" data-sentry-source-file="drawer.tsx" />;

/** Muted description text below the title. */
const DrawerDescription = ({
  className,
  ref,
  ...props
}: React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Description> & {
  ref?: Ref<React.ElementRef<typeof DrawerPrimitive.Description>>;
}) => <DrawerPrimitive.Description className={cn("text-gray-500 text-sm dark:text-gray-400", className)} ref={ref} {...props} data-sentry-element="DrawerPrimitive.Description" data-sentry-component="DrawerDescription" data-sentry-source-file="drawer.tsx" />;
export { Drawer, DrawerClose, DrawerContent, DrawerDescription, DrawerFooter, DrawerHeader, DrawerOverlay, DrawerPortal, DrawerTitle, DrawerTrigger };